/**
 * WorkspaceEditView — Side-by-side markdown editor with live preview
 *
 * Extracted from MainWorkspace to reduce component size.
 * Shows source textarea on left, rendered preview on right,
 * with Save/Override/Cancel/Undo/Redo actions.
 *
 * @module @humanizer/studio/components/workspace/WorkspaceEditView
 */

import { MathMarkdown } from '../markdown';

export interface WorkspaceEditViewProps {
  editContent: string;
  onEditContentChange: (value: string) => void;
  onApplyChanges: () => void;
  onSaveOverride: () => void;
  onCancel: () => void;
  onUndo: () => void;
  onRedo: () => void;
  isSaving: boolean;
  isSavingOverride: boolean;
  canUndo: boolean;
  canRedo: boolean;
  /** Show "Save as Override" button when a node ID is available */
  hasNodeId: boolean;
}

export function WorkspaceEditView({
  editContent,
  onEditContentChange,
  onApplyChanges,
  onSaveOverride,
  onCancel,
  onUndo,
  onRedo,
  isSaving,
  isSavingOverride,
  canUndo,
  canRedo,
  hasNodeId,
}: WorkspaceEditViewProps) {
  return (
    <div className="workspace__split-editor">
      <div className="workspace__split-editor-row">
        <div className="workspace__split-editor-pane workspace__split-editor-pane--source">
          <div className="workspace__split-editor-header">
            <span className="workspace__format-badge">MD</span>
            <span>Source</span>
          </div>
          <textarea
            className="workspace__split-editor-textarea"
            value={editContent}
            onChange={(e) => onEditContentChange(e.target.value)}
            placeholder="Edit markdown content..."
            spellCheck={false}
          />
        </div>
        <div className="workspace__split-editor-divider" />
        <div className="workspace__split-editor-pane workspace__split-editor-pane--preview">
          <div className="workspace__split-editor-header">Preview</div>
          <article className="workspace__split-editor-preview workspace__article">
            <MathMarkdown>{editContent}</MathMarkdown>
          </article>
        </div>
      </div>
      <div className="workspace__editor-actions">
        <button
          className="workspace__editor-btn workspace__editor-btn--primary"
          onClick={onApplyChanges}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save to Buffer'}
        </button>
        {hasNodeId && (
          <button
            className="workspace__editor-btn workspace__editor-btn--primary"
            onClick={onSaveOverride}
            disabled={isSavingOverride}
            title="Save edit as a permanent override for this content"
          >
            {isSavingOverride ? 'Saving...' : 'Save as Override'}
          </button>
        )}
        <button
          className="workspace__editor-btn workspace__editor-btn--secondary"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </button>
        {canUndo && (
          <button
            className="workspace__editor-btn"
            onClick={onUndo}
            disabled={isSaving}
            title="Undo (Cmd+Z)"
          >
            Undo
          </button>
        )}
        {canRedo && (
          <button
            className="workspace__editor-btn"
            onClick={onRedo}
            disabled={isSaving}
            title="Redo (Cmd+Shift+Z)"
          >
            Redo
          </button>
        )}
      </div>
    </div>
  );
}
